import { Box, Pagination } from '@mui/material';
import { FIXFieldsSelector } from 'components/FIXFieldsSelector';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { Scaffold } from 'components/Scaffold';
import { Table } from 'components/Table';
import { Column } from 'components/Table/types/column';
import { SortDirection } from 'components/Table/types/sortDirection';
import { ProcessingType } from 'enums/processingState';
import deepEqual from 'fast-deep-equal';
import { usePagination } from 'hooks/usePagination';
import { useTable } from 'hooks/useTable';
import moment, { Moment } from 'moment';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  executionsProcessingStateSelector,
  executionsSelector,
} from 'redux/reducers/executionsReducer';
import { fetchExecutionsAction } from 'redux/services/executionsService';
import columnDefinitions from 'routes/Executions/columns';
import {
  buildExecutionColumnsFromTags,
  columnsToSelectedFixTags,
  defaultExecutionFixTags,
} from 'routes/Executions/executionColumns';
import { Filters } from 'routes/Executions/Filters';
import styles from 'routes/Messages/messages-route.module.scss';
import { Execution, SummaryReportQuery } from 'types/summaryReport';

export const Executions: React.FC = (): React.ReactElement => {
  const dispatch = useDispatch<any>();
  const processingState = useSelector(executionsProcessingStateSelector);
  const executions = useSelector(executionsSelector);
  const [venue, setVenue] = React.useState<string>('');
  const [query, setQuery] = React.useState<SummaryReportQuery>(initialQuery);
  const [sortedColumns, setSortedColumns] = React.useState<
    Record<keyof Execution, SortDirection> | undefined
  >();
  const [columnFilters, setColumnFilters] = React.useState<
    Record<keyof Execution, string> | undefined
  >();
  const tableProps = useTable<Execution>('executions', columnDefinitions);
  const { columns: storedColumns, onColumnResized, onColumnsChanged } = tableProps;

  const [selectedFixTags, setSelectedFixTags] = React.useState<readonly string[] | undefined>(
    undefined
  );

  const effectiveFixTags = React.useMemo((): readonly string[] => {
    if (selectedFixTags !== undefined) {
      return selectedFixTags;
    }

    if (storedColumns.length > 0) {
      return columnsToSelectedFixTags(storedColumns);
    }

    return defaultExecutionFixTags();
  }, [selectedFixTags, storedColumns]);

  const displayColumns = React.useMemo((): Array<Column<Execution>> => {
    const built = buildExecutionColumnsFromTags(effectiveFixTags);

    return built.map((column: Column<Execution>): Column<Execution> => {
      const stored = storedColumns.find(
        (storedColumn: Column<Execution>): boolean => storedColumn.key === column.key
      );

      if (stored?.weight !== undefined && stored.weight !== column.weight) {
        return { ...column, weight: stored.weight };
      }

      return column;
    });
  }, [effectiveFixTags, storedColumns]);

  const filterFn = React.useCallback(
    (row: Execution): boolean => {
      if (venue !== '' && row.venue !== venue) {
        return false;
      }

      if (!columnFilters) {
        return true;
      }

      const entries = Object.entries(columnFilters);
      return entries.reduce((result: boolean, [key, value]: [string, any]): boolean => {
        if (result === false) {
          return false;
        }

        const rowValue = row[key as keyof Execution];
        if (key === 'side') {
          const sideLabels: { [key: string]: string } = {
            '1': 'Buy',
            '2': 'Sell',
            '3': 'Buy minus',
            '4': 'Sell plus',
            '5': 'Sell short',
            '6': 'Sell short exempt',
            '7': 'Undisclosed',
            '8': 'Cross',
            '9': 'Cross short',
            A: 'Cross short exempt',
            B: 'As Defined',
            C: 'Opposite',
          };
          const displayValue = sideLabels[rowValue as string] || '';
          return displayValue.toLowerCase().includes(value.trim().toLowerCase());
        } else if (typeof rowValue === 'string' || typeof rowValue === 'number') {
          return rowValue.toString().trim().toLowerCase().includes(value.trim().toLowerCase());
        } else if (rowValue instanceof Date) {
          const asMoment = moment(rowValue);
          const asText = asMoment.format('MM/DD/YYYY');

          return asText.includes(value);
        } else {
          return false;
        }
      }, true);
    },
    [columnFilters, venue]
  );

  const sortFn = React.useCallback(
    (row1: Execution, row2: Execution): number => {
      if (!sortedColumns) {
        return 0;
      }

      const entries = Object.entries(sortedColumns);
      return entries.reduce((sum: number, [key, direction]: [string, SortDirection]): number => {
        const value1 = row1[key as keyof Execution];
        const value2 = row2[key as keyof Execution];
        if (typeof value1 === 'string' && typeof value2 === 'string') {
          return value1.localeCompare(value2) * (direction === SortDirection.Ascending ? 1 : -1);
        } else if (typeof value1 === 'number' && typeof value2 === 'number') {
          return (value1 - value2) * (direction === SortDirection.Ascending ? 1 : -1);
        } else {
          return sum;
        }
      }, 0);
    },
    [sortedColumns]
  );

  const [page, setPage] = usePagination(executions, filterFn, sortFn);

  React.useEffect((): VoidFunction => {
    return dispatch(fetchExecutionsAction(query));
  }, [dispatch, query]);

  const handlePageChange = useCallback(
    (_: React.ChangeEvent<unknown>, value: number) => {
      setPage(value);
    },
    [setPage]
  );

  const handleSort = React.useCallback((key: keyof Execution, direction: SortDirection): void => {
    setSortedColumns(
      (
        sortedColumns: Record<keyof Execution, SortDirection>
      ): Record<keyof Execution, SortDirection> => {
        if (direction === SortDirection.None) {
          const { [key]: _, ...resultingColumns } = sortedColumns;
          return resultingColumns as Record<keyof Execution, SortDirection>;
        }

        return {
          ...sortedColumns,
          [key]: direction,
        };
      }
    );
  }, []);

  const handleColumnFiltered = React.useCallback(
    (columnKey: keyof Execution, value: string): void => {
      setColumnFilters(
        (columnFilters: Record<keyof Execution, string>): Record<keyof Execution, string> => {
          if (value.trim() === '') {
            const { [columnKey]: _, ...resultingColumns } = columnFilters;
            return resultingColumns as Record<keyof Execution, string>;
          }

          return {
            ...columnFilters,
            [columnKey]: value.trim(),
          };
        }
      );
    },
    []
  );

  const handleFieldsSelectionChange = React.useCallback(
    (selection: readonly string[]): void => {
      setSelectedFixTags(selection);
      onColumnsChanged(buildExecutionColumnsFromTags(selection));
    },
    [onColumnsChanged]
  );

  const handleResetOriginalLayout = React.useCallback((): void => {
    const defaults = defaultExecutionFixTags();
    setSelectedFixTags(defaults);
    tableProps.onResetOriginalLayout?.();
    onColumnsChanged(buildExecutionColumnsFromTags(defaults));
  }, [onColumnsChanged, tableProps]);

  const handleFiltersChange = React.useCallback(
    (filters: SummaryReportQuery, newVenue: string): void => {
      if (!deepEqual(filters, query)) {
        setQuery(filters);
      }

      setVenue((oldVenue: string): string => {
        if (oldVenue === newVenue) {
          return oldVenue;
        }

        return newVenue;
      });
    },
    [query]
  );

  return (
    <Scaffold title="Executions Blotter">
      <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={0.5}>
        <Filters filters={query} venue={venue} onChange={handleFiltersChange} />
        <div className={styles.toolbarContainer}>
          <div className={styles.toolbarFooter}>
            <FIXFieldsSelector
              selection={effectiveFixTags}
              onSelectionChange={handleFieldsSelectionChange}
            />
          </div>
        </div>
      </Box>
      <Table
        columns={displayColumns}
        rows={page.rows}
        sorting={sortedColumns}
        onColumnSorted={handleSort}
        onFiltered={handleColumnFiltered}
        onResetOriginalLayout={handleResetOriginalLayout}
        onColumnMoved={tableProps.onColumnMoved}
        columnsOrder={tableProps.columnsOrder}
        onColumnResized={onColumnResized}
      />
      <Box sx={paginatorSx}>
        <Pagination
          count={page.totalPageCount}
          page={page.currentPage}
          onChange={handlePageChange}
        />
      </Box>
      <LoadingOverlay loading={processingState.processingType === ProcessingType.processing} />
    </Scaffold>
  );
};

const paginatorSx = {
  marginTop: 2,
  display: 'flex',
  justifyContent: 'center',
};

const startOfToday = (): Moment => {
  return moment().set('hours', 0).set('minutes', 0).set('seconds', 0);
};

const endOfToday = (): Moment => {
  return moment().set('hours', 23).set('minutes', 59).set('seconds', 59);
};

const initialQuery: SummaryReportQuery = {
  from_time: startOfToday(),
  to_time: endOfToday(),
  assetType: '',
};
